"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.shouldNeverHappen = shouldNeverHappen;
exports.failMe = failMe;
exports.switchOn = switchOn;
exports.errorLike = errorLike;
exports.pair = pair;
/**
 * Checks, at compile time, that a certain situation cannot happen (and fail the build if it can). This is useful for
 * checking that all possible cases of a union type are handled.
 *
 * Typical usage pattern:
 *
 *   type X = 'A' | 'B'
 *
 *   function f(x: X) {
 *     if (x === 'a') {
 *       // do something ...
 *       return
 *     }
 *     if (x === 'b') {
 *       // do something ...
 *       return
 *     }
 *     shouldNeverHappen(x)
 *   }
 *
 *
 * If we ever change X such that it has a third case (as in `type X = 'A' | 'B' | 'C') we will get a compile error
 * unless we add logic to this function (`f()`) to handle it.
 *
 * @param n a value of type `never`
 */
function shouldNeverHappen(n) {
    return n;
}
/**
 * An always-failing function. If it ever gets called, it will throw an error. It is useful in conditional expressions
 * in which one of the branches of the expression is the happy path, and the other branch is a sad path in which no
 * value can be computed. Sepcifically, it usually appears as the right-hand-side operand of `??` or `||` expressions.
 *
 * Typical usage pattern:
 *
 *    const dir: string = process.env['WORKING_DIR'] || failMe('missing env variable "a"')
 *
 * Essentially, this is a concise alternative to:
 *
 *    const dir = process.env['a']
 *    if (!dir) {
 *      throw new Error('missing env variable "a"')
 *    }
 *
 * @param hint an optional human-readable string to be placed in the message of the thrown error
 */
function failMe(hint) {
    if (!hint) {
        throw new Error(`This expression must never be evaluated`);
    }
    throw new Error(`Bad value: ${hint}`);
}
/**
 * Evaluates just one of several functions (the `cases` parameter) based on the `selector` value passed in.
 * `cases` is a record of zero-argument functions. The function at `cases[selector]` will be evaluated and its return
 * value is returned back to the caller.
 *
 * A compile time error is produced if not all possible values of `selector` are covered by `cases`.
 *
 * Example:
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *        '*': () => n1 * n2,
 *      })
 *    }
 *
 *
 * The following snippet yields a compile-time error:
 *
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *      })   // <-- Compiler error here due to missing case ('*')
 *    }
 *
 * And so does this:
 *
 *    function f(op: '+' | '-' | '*', n1: number, n2: number) {
 *      return switchOn(op, {
 *        '+': () => n1 + n2,
 *        '-': () => n1 - n2,
 *        '*': () => n1 - n2,
 *        '/': () => n1 / n2,
 *      })   // <-- Compiler error here due to extraneous case ('/')
 *    }
 *
 * @param selector
 * @param cases
 * @returns
 */
function switchOn(selector, cases) {
    const f = cases[selector];
    return f();
}
function errorLike(err, fallbackMessage) {
    const { message, stack, reason } = (err ?? {});
    return {
        message: typeof message === 'string' ? message : fallbackMessage,
        stack: typeof stack === 'string' ? stack : undefined,
        reason: typeof reason === 'string' ? reason : undefined,
    };
}
/**
 * Generates a two-tuple from the arguments passed in. Useful in situations where the compiler fails to infer that
 * `[foo, bar]` is a tuple and treats it like an array. Using `pair(foo, bar)` will make the compiler correctly infer
 * the tuple type.
 */
function pair(a, b) {
    return [a, b];
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29uc3RydWN0cy5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9jb25zdHJ1Y3RzLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiI7O0FBMEJBLDhDQUVDO0FBb0JELHdCQU1DO0FBMkNELDRCQUdDO0FBd0JELDhCQU9DO0FBT0Qsb0JBRUM7QUE1SUQ7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0F5Qkc7QUFDSCxTQUFnQixpQkFBaUIsQ0FBQyxDQUFRO0lBQ3hDLE9BQU8sQ0FBQyxDQUFBO0FBQ1YsQ0FBQztBQUVEOzs7Ozs7Ozs7Ozs7Ozs7OztHQWlCRztBQUNILFNBQWdCLE1BQU0sQ0FBQyxJQUFhO0lBQ2xDLElBQUksQ0FBQyxJQUFJLEVBQUUsQ0FBQztRQUNWLE1BQU0sSUFBSSxLQUFLLENBQUMseUNBQXlDLENBQUMsQ0FBQTtJQUM1RCxDQUFDO0lBRUQsTUFBTSxJQUFJLEtBQUssQ0FBQyxjQUFjLElBQUksRUFBRSxDQUFDLENBQUE7QUFDdkMsQ0FBQztBQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBd0NHO0FBQ0gsU0FBZ0IsUUFBUSxDQUFzQixRQUFXLEVBQUUsS0FBeUI7SUFDbEYsTUFBTSxDQUFDLEdBQUcsS0FBSyxDQUFDLFFBQVEsQ0FBQyxDQUFBO0lBQ3pCLE9BQU8sQ0FBQyxFQUFFLENBQUE7QUFDWixDQUFDO0FBd0JELFNBQWdCLFNBQVMsQ0FBQyxHQUFZLEVBQUUsZUFBd0I7SUFDOUQsTUFBTSxFQUFFLE9BQU8sRUFBRSxLQUFLLEVBQUUsTUFBTSxFQUFFLEdBQUcsQ0FBQyxHQUFHLElBQUksRUFBRSxDQUFjLENBQUE7SUFDM0QsT0FBTztRQUNMLE9BQU8sRUFBRSxPQUFPLE9BQU8sS0FBSyxRQUFRLENBQUMsQ0FBQyxDQUFDLE9BQU8sQ0FBQyxDQUFDLENBQUMsZUFBZTtRQUNoRSxLQUFLLEVBQUUsT0FBTyxLQUFLLEtBQUssUUFBUSxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDLFNBQVM7UUFDcEQsTUFBTSxFQUFFLE9BQU8sTUFBTSxLQUFLLFFBQVEsQ0FBQyxDQUFDLENBQUMsTUFBTSxDQUFDLENBQUMsQ0FBQyxTQUFTO0tBQ3hELENBQUE7QUFDSCxDQUFDO0FBRUQ7Ozs7R0FJRztBQUNILFNBQWdCLElBQUksQ0FBTyxDQUFJLEVBQUUsQ0FBSTtJQUNuQyxPQUFPLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFBO0FBQ2YsQ0FBQyJ9